import { FC, useEffect, useRef, useState } from "react";

import { useFormContext, useWatch } from "react-hook-form";

import {
  RateOptionHighlightKey,
  useTriggerRateOptionHighlight,
} from "../../../../RateOptionHighlight/RateOptionHighlightContext";
import { useRatesExpansion } from "../../../../RatesExpansion/RatesExpansionContext";
import { useRateContext } from "../../../Context/RateContext";
import { useRateFieldName } from "../../../hooks/useRateFieldName";
import { useEnableGroupTicket } from "../../../hooks/useEnableGroupTicket";
import { useEnableMemberOnly } from "../../../hooks/useEnableMemberOnly";

import { detectRateSettingIntents, RateSuggestionType } from "./detectRateSettingIntents";
import { getVisibleSuggestionTypes } from "./getVisibleSuggestionTypes";
import { SuggestionBanner } from "./SuggestionBanner/SuggestionBanner";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import {
  getFromLocalStorage,
  getRateSettingSuggestionDismissedKey,
  setInLocalStorage,
} from "@/common/helpers/localStorage";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

export interface RateSuggestionSeed {
  name: string;
  description: string;
}

interface RateSettingSuggestionsProps {
  seed: RateSuggestionSeed;
  field?: "name" | "description";
}

const SUGGESTION_TYPES: RateSuggestionType[] = ["group", "taxReceipt", "memberOnly"];

const ANALYTICS_TYPE: Record<RateSuggestionType, string> = {
  group: "group_ticket",
  taxReceipt: "tax_receipt",
  memberOnly: "members_only",
};

const HIGHLIGHTED_OPTION: Record<RateSuggestionType, RateOptionHighlightKey> = {
  group: "groupTicket",
  taxReceipt: "taxReceipt",
  memberOnly: "memberOnly",
};

export const RateSettingSuggestions: FC<RateSettingSuggestionsProps> = ({ seed, field }) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { rateId, rateIndex, formCategory, isRegistration, hasExistingPayments } = useRateContext();
  const { organization } = useCurrentUserContext();
  const { control, getValues, setValue } = useFormContext<StepTicketsFormData>();
  const nameFieldName = useRateFieldName("name");
  const descriptionFieldName = useRateFieldName("description");
  const { setExpandedRateId } = useRatesExpansion();
  const triggerOptionHighlight = useTriggerRateOptionHighlight();
  const enableGroupTicket = useEnableGroupTicket();
  const { canEnableMemberOnly, enableMemberOnly } = useEnableMemberOnly();

  const isGroup = useWatch({ control, name: useRateFieldName("isGroup") });
  const isGeneratingReceipt = useWatch({ control, name: useRateFieldName("isGeneratingReceipt") });
  const isMemberOnly = useWatch({ control, name: useRateFieldName("isMemberOnly") });
  const isPayWhatYouCan = useWatch({ control, name: useRateFieldName("isPayWhatYouCan") });
  const amount = useWatch({ control, name: useRateFieldName("amount") });

  const [dismissed, setDismissed] = useState<Set<RateSuggestionType>>(
    () =>
      new Set(
        SUGGESTION_TYPES.filter(
          (type) => String(getFromLocalStorage?.(getRateSettingSuggestionDismissedKey(rateId, type), "")) === "true"
        )
      )
  );

  const intents = detectRateSettingIntents({ title: seed.name, description: seed.description });

  const visibleTypes = getVisibleSuggestionTypes({
    intents,
    formCategory,
    amount,
    isPayWhatYouCan: Boolean(isPayWhatYouCan),
    isGroup: Boolean(isGroup),
    isGeneratingReceipt: Boolean(isGeneratingReceipt),
    isMemberOnly: Boolean(isMemberOnly),
    hasExistingPayments,
    canGenerateReceipt: Boolean(organization?.canGenerateReceipt),
    canEnableMemberOnly,
    isRegistration,
    dismissed,
  });

  const firstVisibleType = visibleTypes[0] ?? null;
  const titleIntents = detectRateSettingIntents({ title: seed.name, description: "" });
  const triggerField = firstVisibleType && titleIntents[firstVisibleType] ? "name" : "description";
  const activeType = field === undefined || field === triggerField ? firstVisibleType : null;
  const activeKeyword = activeType ? intents[activeType]?.matchedKeyword : undefined;

  const shownTypesRef = useRef<Set<RateSuggestionType>>(new Set());
  useEffect(() => {
    if (activeType && !shownTypesRef.current.has(activeType)) {
      shownTypesRef.current.add(activeType);
      logAmplitudeEvent(AmplitudeEvents.TicketSuggestionShown, {
        suggestion_type: ANALYTICS_TYPE[activeType],
        form_type: formCategory,
        is_liveform: true,
        rate_id: rateId,
        matched_keyword: activeKeyword,
      });
    }
  }, [activeType, activeKeyword, rateId, formCategory, logAmplitudeEvent]);

  if (!activeType) {
    return null;
  }

  const handleAccept = () => {
    const liveIntents = detectRateSettingIntents({
      title: getValues(nameFieldName) ?? "",
      description: getValues(descriptionFieldName) ?? "",
    });
    const liveIntent = liveIntents[activeType];
    if (!liveIntent) {
      return;
    }
    setInLocalStorage?.(getRateSettingSuggestionDismissedKey(rateId, activeType), true);
    setDismissed((previous) => new Set(previous).add(activeType));
    if (activeType === "group") {
      enableGroupTicket(liveIntents.group?.attendees);
    } else if (activeType === "taxReceipt") {
      setValue(`rates.${rateIndex}.isGeneratingReceipt`, true, { shouldDirty: true });
      const detectedDollars = liveIntents.taxReceipt?.amountDollars;
      if (detectedDollars && typeof amount === "number" && detectedDollars * 100 <= amount) {
        setValue(`rates.${rateIndex}.receiptEligibleAmount`, detectedDollars * 100, { shouldDirty: true });
      }
    } else {
      enableMemberOnly();
    }
    setExpandedRateId(rateId);
    triggerOptionHighlight(rateId, HIGHLIGHTED_OPTION[activeType]);
    logAmplitudeEvent(AmplitudeEvents.TicketSuggestionResolved, {
      suggestion_type: ANALYTICS_TYPE[activeType],
      action: "accepted",
      form_type: formCategory,
      is_liveform: true,
      rate_id: rateId,
      matched_keyword: liveIntent.matchedKeyword,
      detected_attendees: activeType === "group" ? liveIntents.group?.attendees : undefined,
      detected_amount: activeType === "taxReceipt" ? liveIntents.taxReceipt?.amountDollars : undefined,
    });
    logAmplitudeEvent(AmplitudeEvents.TicketOptionToggled, {
      option: ANALYTICS_TYPE[activeType],
      enabled: true,
      entry_point: "suggestion",
      form_type: formCategory,
      is_liveform: true,
    });
  };

  const handleDismiss = () => {
    setInLocalStorage?.(getRateSettingSuggestionDismissedKey(rateId, activeType), true);
    setDismissed((previous) => new Set(previous).add(activeType));
    logAmplitudeEvent(AmplitudeEvents.TicketSuggestionResolved, {
      suggestion_type: ANALYTICS_TYPE[activeType],
      action: "dismissed",
      form_type: formCategory,
      is_liveform: true,
      rate_id: rateId,
      matched_keyword: activeKeyword,
    });
  };

  const copyKeys =
    activeType === "group" && isRegistration
      ? ({
          lead: "ticketing.rateSuggestion.group.lead.registration",
          description: "ticketing.rateSuggestion.group.description.registration",
          acceptCta: "ticketing.rateSuggestion.group.acceptCta.registration",
          dismissCta: "ticketing.rateSuggestion.group.dismissCta",
        } as const)
      : ({
          lead: `ticketing.rateSuggestion.${activeType}.lead`,
          description: `ticketing.rateSuggestion.${activeType}.description`,
          acceptCta: `ticketing.rateSuggestion.${activeType}.acceptCta`,
          dismissCta: `ticketing.rateSuggestion.${activeType}.dismissCta`,
        } as const);

  return (
    <SuggestionBanner
      lead={t("dashboard", copyKeys.lead)}
      description={t("dashboard", copyKeys.description)}
      acceptLabel={t("dashboard", copyKeys.acceptCta)}
      dismissLabel={t("dashboard", copyKeys.dismissCta)}
      onAccept={handleAccept}
      onDismiss={handleDismiss}
    />
  );
};
